import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiLink, FiUsers, FiGrid, FiKey, FiCreditCard, FiActivity, FiFileText, FiServer, FiDroplet, FiAlertCircle, FiCheckCircle, FiClock, FiSearch, FiMenu, FiX, FiShield } from 'react-icons/fi';
import { HiUserGroup } from 'react-icons/hi';
import { useMenuStatuses } from '../../hooks/useMenuStatuses';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../Dashboard/DashboardLayout';
import ConnectionsPage from './ConnectionsPage';
import UsersPage from './UsersPage';
import GroupsPage from './GroupsPage';
import PermissionsPage from './PermissionsPage';
import SubscriptionPage from './SubscriptionPage';
import UsagePage from './UsagePage';
import TeamsPage from './TeamsPage';
import InvoicesPage from './InvoicesPage';
import BYOOPage from './BYOOPage';
import WhiteLabelPage from './WhiteLabelPage';
import SSOPage from './SSOPage';
import PaymentMethodsPage from './PaymentMethodsPage';
import GDPRPage from './GDPRPage';

// Badge de statut pour les items du menu
interface StatusBadgeProps {
  type: 'ok' | 'warning' | 'error' | 'pending' | 'new';
  count?: number;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ type, count }) => {
  const badges = {
    ok: { bg: 'bg-green-100', text: 'text-green-600', icon: FiCheckCircle },
    warning: { bg: 'bg-orange-100', text: 'text-orange-600', icon: FiAlertCircle },
    error: { bg: 'bg-red-100', text: 'text-red-600', icon: FiAlertCircle },
    pending: { bg: 'bg-blue-100', text: 'text-blue-600', icon: FiClock },
    new: { bg: 'bg-purple-100', text: 'text-purple-600', icon: null }
  };

  const badge = badges[type];
  const Icon = badge.icon;

  if (count !== undefined && count === 0) return null;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
      {Icon && <Icon size={10} />}
      {count !== undefined && count > 0 ? count : null}
      {count === undefined && type === 'new' && 'New'}
    </span>
  );
};

const ADMIN_ROLES  = ['owner', 'admin'];
const OWNER_ROLES  = ['owner'];

const SettingsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Récupérer les vrais statuts depuis les APIs
  const { statuses: menuStatuses, loading: statusesLoading } = useMenuStatuses();

  // Fermer le menu mobile lors de la navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const userRole = user?.role || '';

  const allMenuItems = [
    { id: 'connections',     label: t('menu.connections.label'),    icon: FiLink,       path: '/settings/connections',    description: t('menu.connections.description') },
    { id: 'users',           label: t('menu.users.label'),          icon: FiUsers,      path: '/settings/users',          description: t('menu.users.description'),          roles: ADMIN_ROLES },
    { id: 'teams',           label: t('menu.teams.label'),          icon: HiUserGroup,  path: '/settings/teams',          description: t('menu.teams.description'),          roles: ADMIN_ROLES },
    { id: 'groups',          label: t('menu.groups.label'),         icon: FiGrid,       path: '/settings/groups',         description: t('menu.groups.description'),         roles: ADMIN_ROLES },
    { id: 'permissions',     label: t('menu.permissions.label'),    icon: FiKey,        path: '/settings/permissions',    description: t('menu.permissions.description'),    roles: ADMIN_ROLES },
    { id: 'byoo',            label: t('menu.byoo.label'),           icon: FiServer,     path: '/settings/byoo',           description: t('menu.byoo.description'),           roles: ADMIN_ROLES },
    { id: 'white-label',     label: t('menu.whiteLabel.label'),     icon: FiDroplet,    path: '/settings/white-label',    description: t('menu.whiteLabel.description'),     roles: ADMIN_ROLES },
    { id: 'sso',             label: 'SSO',                          icon: FiShield,     path: '/settings/sso',            description: 'Single Sign-On SAML 2.0 / OIDC',    roles: ADMIN_ROLES },
    { id: 'subscription',    label: t('menu.subscription.label'),   icon: FiCreditCard, path: '/settings/subscription',   description: t('menu.subscription.description'),   roles: OWNER_ROLES },
    { id: 'payment-methods', label: t('menu.paymentMethods.label'), icon: FiCreditCard, path: '/settings/payment-methods',description: t('menu.paymentMethods.description'), roles: OWNER_ROLES },
    { id: 'invoices',        label: t('menu.invoices.label'),       icon: FiFileText,   path: '/settings/invoices',       description: t('menu.invoices.description'),        roles: OWNER_ROLES },
    { id: 'gdpr',            label: t('menu.gdpr.label'),           icon: FiShield,     path: '/settings/gdpr',           description: t('menu.gdpr.description') },
    { id: 'usage',           label: t('menu.usage.label'),          icon: FiActivity,   path: '/settings/usage',          description: t('menu.usage.description'),          roles: ADMIN_ROLES },
  ];

  const menuItems = allMenuItems.filter(item =>
    !item.roles || item.roles.includes(userRole)
  );

  const currentPath = location.pathname;

  // Filtrer les items du menu selon la recherche
  const filteredMenuItems = menuItems.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Quick actions filtrées selon le rôle
  const allQuickActions = [
    { label: t('quickActions.viewUsage'),   icon: FiActivity,   path: '/settings/usage',        roles: ADMIN_ROLES },
    { label: t('quickActions.manageSubs'),  icon: FiCreditCard, path: '/settings/subscription', roles: OWNER_ROLES },
    { label: t('quickActions.viewInvoices'),icon: FiFileText,   path: '/settings/invoices',     roles: OWNER_ROLES },
  ];
  const quickActions = allQuickActions.filter(a => a.roles.includes(userRole));

  return (
    <DashboardLayout>
      <div className="flex h-full bg-gray-50 relative">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden fixed bottom-6 right-6 z-50 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition"
        >
          {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>

        {/* Overlay pour mobile */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar Navigation */}
        <div className={`
          w-80 bg-white border-r border-gray-200 flex flex-col
          fixed lg:static inset-y-0 left-0 z-40
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">{t('navigation.title')}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('navigation.subtitle')}</p>
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-b border-gray-200 bg-gradient-to-br from-blue-50 to-purple-50">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('quickActions.title') || 'Actions rapides'}</h3>
            <div className="space-y-1">
              {quickActions.map((action, idx) => {
                const Icon = action.icon;
                return (
                  <button
                    key={idx}
                    onClick={() => navigate(action.path)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white hover:shadow-sm rounded-lg transition"
                  >
                    <Icon size={16} className="text-blue-600" />
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={t('search.placeholder') || 'Rechercher...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path;
              const status = menuStatuses[item.id];

              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-start gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-blue-50 text-blue-600 border border-blue-200 shadow-sm'
                      : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <Icon size={20} className={`mt-0.5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div className="flex-1 text-left">
                    <div className={`font-medium flex items-center justify-between gap-2 ${isActive ? 'text-blue-600' : 'text-gray-900'}`}>
                      <span>{item.label}</span>
                      {status && <StatusBadge type={status.type} count={status.count} />}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {item.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <Routes>
            {/* Accessible à tous */}
            <Route path="connections" element={<ConnectionsPage />} />
            <Route path="gdpr"        element={<GDPRPage />} />

            {/* owner + admin uniquement */}
            <Route path="users"       element={ADMIN_ROLES.includes(userRole) ? <UsersPage />       : <Navigate to="/settings" replace />} />
            <Route path="teams"       element={ADMIN_ROLES.includes(userRole) ? <TeamsPage />       : <Navigate to="/settings" replace />} />
            <Route path="groups"      element={ADMIN_ROLES.includes(userRole) ? <GroupsPage />      : <Navigate to="/settings" replace />} />
            <Route path="permissions" element={ADMIN_ROLES.includes(userRole) ? <PermissionsPage /> : <Navigate to="/settings" replace />} />
            <Route path="byoo"        element={ADMIN_ROLES.includes(userRole) ? <BYOOPage />        : <Navigate to="/settings" replace />} />
            <Route path="white-label" element={ADMIN_ROLES.includes(userRole) ? <WhiteLabelPage />  : <Navigate to="/settings" replace />} />
            <Route path="sso"         element={ADMIN_ROLES.includes(userRole) ? <SSOPage />         : <Navigate to="/settings" replace />} />
            <Route path="usage"       element={ADMIN_ROLES.includes(userRole) ? <UsagePage />       : <Navigate to="/settings" replace />} />

            {/* owner uniquement */}
            <Route path="subscription"    element={OWNER_ROLES.includes(userRole) ? <SubscriptionPage />   : <Navigate to="/settings" replace />} />
            <Route path="payment-methods" element={OWNER_ROLES.includes(userRole) ? <PaymentMethodsPage /> : <Navigate to="/settings" replace />} />
            <Route path="invoices"        element={OWNER_ROLES.includes(userRole) ? <InvoicesPage />       : <Navigate to="/settings" replace />} />

            <Route path="/" element={<WelcomePage />} />
          </Routes>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Welcome page when no section is selected
const WelcomePage: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || '';
  const isAdmin = ADMIN_ROLES.includes(role);
  const isOwner = OWNER_ROLES.includes(role);

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('welcome.title')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition cursor-pointer" onClick={() => navigate('/settings/connections')}>
          <FiLink size={32} className="text-blue-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('welcome.connections.title')}</h3>
          <p className="text-gray-600 text-sm">{t('welcome.connections.description')}</p>
        </div>

        {isAdmin && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition cursor-pointer" onClick={() => navigate('/settings/users')}>
            <FiUsers size={32} className="text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('welcome.users.title')}</h3>
            <p className="text-gray-600 text-sm">{t('welcome.users.description')}</p>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition cursor-pointer" onClick={() => navigate('/settings/teams')}>
            <HiUserGroup size={32} className="text-indigo-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('welcome.teams.title')}</h3>
            <p className="text-gray-600 text-sm">{t('welcome.teams.description')}</p>
          </div>
        )}

        {isOwner && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition cursor-pointer" onClick={() => navigate('/settings/subscription')}>
            <FiCreditCard size={32} className="text-orange-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('welcome.subscription.title')}</h3>
            <p className="text-gray-600 text-sm">{t('welcome.subscription.description')}</p>
          </div>
        )}

        {isOwner && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition cursor-pointer" onClick={() => navigate('/settings/payment-methods')}>
            <FiCreditCard size={32} className="text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('welcome.paymentMethods.title')}</h3>
            <p className="text-gray-600 text-sm">{t('welcome.paymentMethods.description')}</p>
          </div>
        )}

        {isOwner && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition cursor-pointer" onClick={() => navigate('/settings/invoices')}>
            <FiFileText size={32} className="text-pink-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('welcome.invoices.title')}</h3>
            <p className="text-gray-600 text-sm">{t('welcome.invoices.description')}</p>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition cursor-pointer" onClick={() => navigate('/settings/usage')}>
            <FiActivity size={32} className="text-teal-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('welcome.usage.title')}</h3>
            <p className="text-gray-600 text-sm">{t('welcome.usage.description')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
